/**
 * Vincent #288 (2026-06-11) — Page publique de réinitialisation de mot de passe.
 * Atteinte via le lien envoyé par courriel : /reset-password?token=...
 * L'utilisateur crée et confirme un nouveau mot de passe, puis est redirigé
 * vers la page de connexion.
 */
import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LockKey, CheckCircle, SpinnerGap } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success("Mot de passe réinitialisé avec succès");
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="bg-white rounded-2xl shadow-xl border border-zinc-200 max-w-md w-full p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[#002FA7]/10 flex items-center justify-center">
            <LockKey size={22} weight="duotone" className="text-[#002FA7]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-zinc-900">Réinitialiser votre accès</h1>
            <p className="text-xs text-zinc-500">Knoria — plateforme de formation</p>
          </div>
        </div>

        {!token ? (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3" data-testid="reset-password-no-token">
            Lien invalide : aucun jeton de réinitialisation détecté. Demandez à votre gestionnaire de vous renvoyer un lien.
          </div>
        ) : done ? (
          <div className="text-center py-6" data-testid="reset-password-success">
            <CheckCircle size={48} weight="fill" className="text-teal-500 mx-auto mb-3" />
            <p className="font-semibold text-zinc-900 mb-1">Mot de passe réinitialisé !</p>
            <p className="text-sm text-zinc-500 mb-4">Vous allez être redirigé vers la page de connexion…</p>
            <Link to="/login" className="text-sm text-[#002FA7] font-semibold hover:underline">
              Se connecter maintenant →
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1">Nouveau mot de passe</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-[#002FA7]/40 focus:border-[#002FA7] outline-none"
                placeholder="Minimum 8 caractères"
                data-testid="reset-password-input"
                autoComplete="new-password"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1">Confirmer le mot de passe</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-[#002FA7]/40 focus:border-[#002FA7] outline-none"
                placeholder="Retapez le même mot de passe"
                data-testid="reset-password-confirm-input"
                autoComplete="new-password"
                required
              />
            </div>
            {error && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="reset-password-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-2.5 rounded-lg bg-[#002FA7] hover:bg-[#00248a] text-white font-semibold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="reset-password-submit-btn"
            >
              {submitting && <SpinnerGap size={16} className="animate-spin" />}
              Réinitialiser mon mot de passe
            </button>
            <p className="text-[11px] text-zinc-400 text-center">
              Le lien de réinitialisation est valide 1 heure et à usage unique.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
